/**
 * Main Application Component
 */

import React from 'react';
import { Admin, CustomRoutes, Resource } from 'react-admin';
import { Route } from 'react-router-dom';
import { StyledEngineProvider } from '@mui/material/styles';
import {
  Business as OrganizationIcon,
  SupportAgent as StaffIcon,
  AccountTree as DepartmentIcon,
  AssignmentInd as StaffScheduleIcon,
  QrCode2 as QrCodeIcon,
  History as CallHistoryIcon,
  LanOutlined as ConnectionIcon,
  ManageAccounts as OrgManagerIcon,
  HowToRegOutlined as SignupRequestIcon,
} from '@mui/icons-material';

// Providers
import { authProvider, dataProvider, i18nProvider } from './providers';
import { canAccessFeature } from './utils/access';

// Layout
import { CustomLayout } from './layouts';
import CustomLoginPage from './components/auth/CustomLoginPage';

// Theme
import { adminTheme as theme } from './themes';

// Features
import { OrganizationList } from './features/organizations/components/OrganizationList';
import { OrganizationCreate } from './features/organizations/components/OrganizationCreate';
import { OrganizationEdit } from './features/organizations/components/OrganizationEdit';
import { StaffList } from './features/staff/components/StaffList';
import { StaffCreate } from './features/staff/components/StaffCreate';
import { StaffEdit } from './features/staff/components/StaffEdit';
import { DepartmentList } from './features/departments/components/DepartmentList';
import { DepartmentCreate } from './features/departments/components/DepartmentCreate';
import { DepartmentEdit } from './features/departments/components/DepartmentEdit';
import { StaffScheduleList } from './features/staffSchedule/components/StaffScheduleList';
import { StaffScheduleCreate } from './features/staffSchedule/components/StaffScheduleCreate';
import { StaffScheduleEdit } from './features/staffSchedule/components/StaffScheduleEdit';
import { QrCodeList } from './features/qrcodes/components/QrCodeList';
import { QrCodeCreate } from './features/qrcodes/components/QrCodeCreate';
import { QrCodeEdit } from './features/qrcodes/components/QrCodeEdit';
import { QrCodeShow } from './features/qrcodes/components/QrCodeShow';
import { PlanList } from './features/plans/components/PlanList';
import { PlanCreate } from './features/plans/components/PlanCreate';
import { PlanEdit } from './features/plans/components/PlanEdit';
import { TransactionList } from './features/payments/components/TransactionList';
import { SystemPage } from './features/system/components/SystemPage';
import { ServerSettingsPage } from './features/serverSettings/components/ServerSettingsPage';
import { ClientList } from './features/clients/components/ClientList';
import { ClientCreate } from './features/clients/components/ClientCreate';
import { ClientEdit } from './features/clients/components/ClientEdit';
import { SignupRequestList } from './features/signupRequests/components/SignupRequestList';
import { AdminList } from './features/admins/components/AdminList';
import { AdminCreate } from './features/admins/components/AdminCreate';
import { AdminEdit } from './features/admins/components/AdminEdit';
import { CallerFieldList } from './features/callerFields/components/CallerFieldList';
import { CallerFieldCreate, CallerFieldEdit } from './features/callerFields/components/CallerFieldForm';
import { ProfilePage } from './features/profile/components/ProfilePage';
import { CallHistoryList } from './features/callHistory/components/CallHistoryList';
import { ConnectionList } from './features/connections/components/ConnectionList';
import { OrgManagerList } from './features/orgManagers/components/OrgManagerList';
import { OrgManagerCreate } from './features/orgManagers/components/OrgManagerCreate';
import { OrgManagerEdit } from './features/orgManagers/components/OrgManagerEdit';
import Dashboard from './features/dashboard/Dashboard';

/**
 * Main App Component
 */
const App: React.FC = () => {
  return (
    <StyledEngineProvider injectFirst>
      <Admin
        authProvider={authProvider}
        dataProvider={dataProvider}
        i18nProvider={i18nProvider}
        theme={theme}
        layout={CustomLayout}
        loginPage={CustomLoginPage}
        dashboard={Dashboard}
        requireAuth
        disableTelemetry
      >
        {/*
          Resources are declared conditionally by role + plan module. A
          resource the caller cannot access is not registered at all, so it
          is unreachable by URL (not merely hidden from the menu). The
          backend independently scopes/authorizes every request.
        */}
        {() => (
          <>
            <Resource name="callDetails" list={CallHistoryList} icon={CallHistoryIcon} />
            {canAccessFeature('organizations') && (
              <Resource
                name="organizations"
                list={OrganizationList}
                create={OrganizationCreate}
                edit={OrganizationEdit}
                icon={OrganizationIcon}
              />
            )}
            {canAccessFeature('departments') && (
              <Resource
                name="departments"
                list={DepartmentList}
                create={DepartmentCreate}
                edit={DepartmentEdit}
                icon={DepartmentIcon}
              />
            )}
            {canAccessFeature('staff') && (
              <Resource
                name="staff"
                list={StaffList}
                create={StaffCreate}
                edit={StaffEdit}
                icon={StaffIcon}
              />
            )}
            {canAccessFeature('userGroupOrganization') && (
              <Resource
                name="userGroupOrganization"
                list={StaffScheduleList}
                create={StaffScheduleCreate}
                edit={StaffScheduleEdit}
                icon={StaffScheduleIcon}
              />
            )}
            {canAccessFeature('qrcodes') && (
              <Resource
                name="qrcodes"
                list={QrCodeList}
                create={QrCodeCreate}
                edit={QrCodeEdit}
                show={QrCodeShow}
                icon={QrCodeIcon}
              />
            )}
            {canAccessFeature('connections') && (
              <Resource name="connections" list={ConnectionList} icon={ConnectionIcon} />
            )}
            {canAccessFeature('orgManagers') && (
              <Resource
                name="orgManagers"
                list={OrgManagerList}
                create={OrgManagerCreate}
                edit={OrgManagerEdit}
                icon={OrgManagerIcon}
              />
            )}
            {canAccessFeature('plans') && (
              <Resource name="plans" list={PlanList} create={PlanCreate} edit={PlanEdit} />
            )}
            {canAccessFeature('transactions') && (
              <Resource name="transactions" list={TransactionList} />
            )}
            {canAccessFeature('clients') && (
              <Resource name="clients" list={ClientList} create={ClientCreate} edit={ClientEdit} />
            )}
            {/* List only: the API has no single-record read for a request,
                and a request is decided from the tray, never edited. */}
            {canAccessFeature('signupRequests') && (
              <Resource
                name="signupRequests"
                list={SignupRequestList}
                icon={SignupRequestIcon}
              />
            )}
            {canAccessFeature('admins') && (
              <Resource name="admins" list={AdminList} create={AdminCreate} edit={AdminEdit} />
            )}
            {canAccessFeature('qrUserDetails') && (
              <Resource
                name="qrUserDetails"
                list={CallerFieldList}
                create={CallerFieldCreate}
                edit={CallerFieldEdit}
              />
            )}
            <CustomRoutes>
              {canAccessFeature('system') && (
                <Route path="/system" element={<SystemPage />} />
              )}
              {canAccessFeature('server-settings') && (
                <Route path="/server-settings" element={<ServerSettingsPage />} />
              )}
              <Route path="/profile" element={<ProfilePage />} />
            </CustomRoutes>
          </>
        )}
      </Admin>
    </StyledEngineProvider>
  );
};

export default App;
